<script lang="ts">
import { orderService } from "@/app/services/OrderService";
import { useLoader } from "@/utils/loader";
import { useErrorModal } from "@/utils/modal";
import { ref, defineComponent, computed } from "vue";
import SearchTargetingOrders from "@/views/orders/SearchTargetingOrders/SearchTargetingOrders.vue";
import OrderTab from "@/components/organisms/orders/OrderTab.vue";
import { orderStore } from "@/store/order";
import { authStore } from "@/store/auth";
import Ad from "@/components/organisms/common/Ad.vue";

export default defineComponent({
  components: {
    SearchTargetingOrders,
    OrderTab,
    Ad
  },
  setup() {
    const loader = useLoader();
    const { showErrorModal } = useErrorModal();

    const existsIndividualOfferOrders = ref<boolean>(false);

    const fetchData = async () => {
      loader.show();
      try {
        await Promise.all([
          fetchCountOfIndividualOfferOrders(),
          fetchLimitedOrders("reloadIfCacheExpired")
        ]);
      } catch (e) {
        await showErrorModal(e as Error);
      } finally {
        loader.hide();
      }
    };

    const fetchLimitedOrders = async (
      fetchType: "append" | "reloadIfCacheExpired"
    ) => {
      if (!authStore.hasRegisteredPersonalInfo) {
        return;
      }

      if (fetchType === "reloadIfCacheExpired") {
        orderStore.clearEvaluations();
      }

      loader.show();
      try {
        // MEMO: 返り値の SlimOrder[] を ref に格納して参照するような形にすると、
        // 開発時のホットリロードでリストが初期化されてしまうため、
        // コンポーネントに渡す案件リストはストアから computed で参照する
        // これはホットリロード時に beforeRouteEnter が発火せず、fetchLimitedOrders が呼ばれないため
        await orderService.fetchLimitedOrders(fetchType);
      } catch (e) {
        await showErrorModal(e as Error);
      } finally {
        loader.hide();
      }
    };

    const fetchCountOfIndividualOfferOrders = async () => {
      if (!authStore.hasRegisteredPersonalInfo) {
        return;
      }
      loader.show();
      //
      try {
        const {
          count
        } = await orderService.fetchCountOfIndividualOfferOrders();
        existsIndividualOfferOrders.value = count > 0;
      } catch (e) {
        await showErrorModal(e as Error);
      } finally {
        loader.hide();
      }
    };

    const isAllOrdersLoaded = computed(
      () => orderStore.isAllLimitedOrderLoaded
    );
    const limitedOrders = computed(
      () => orderStore.limitedOrderListCache ?? []
    );

    return {
      limitedOrders,
      existsIndividualOfferOrders,
      isAllOrdersLoaded,
      fetchCountOfIndividualOfferOrders,
      fetchData,
      fetchLimitedOrders
    };
  },
  beforeRouteEnter(to, from, next) {
    next(async vm => {
      const instance = vm;
      if (from.name === "OrdersDetail") {
        // `OrdersDetail`から遷移してきた場合は元のスクロール位置に戻したい
        // そのため案件一覧を再取得せず遷移前のまま表示させる。件数のみ再計算
        await Promise.all([
          // @ts-ignore
          instance.fetchCountOfIndividualOfferOrders()
        ]);
        return;
      }
      // @ts-ignore
      await instance.fetchData();
    });
  }
});
</script>

<template>
  <SearchTargetingOrders
    :orders="limitedOrders"
    :isAllOrdersLoaded="isAllOrdersLoaded"
    transitionSource="SearchLimitedOrders"
    pageTitleWhenEmpty="限定公開がありません"
    @fetch-orders="() => fetchLimitedOrders('append')"
  >
    <template v-slot:orderTab>
      <OrderTab
        :orderType="'limited'"
        :existsLimitedOrder="limitedOrders.length > 0"
        :existsIndividualOrder="existsIndividualOfferOrders"
      />
    </template>

    <template v-slot:ad>
      <!-- AD: search_limited_order -->
      <Ad adSlot="search_limited_order" class="ad-slot-search" />
    </template>

    <template v-slot:emptyDescription>
      勤務した事業所から評価されると <br />
      限定公開が配信されます <br />
      実績を重ねて限定公開を獲得しましょう！
    </template>
  </SearchTargetingOrders>
</template>

<style lang="scss" scoped>
.ad-slot-search {
  margin: 6px 8px 0;
}
</style>
